import { useCallback, useMemo, useState } from 'react';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IManagedUserAccessDataModel } from '../../../data-models/managed-user-access.data-model';
import { IUserDataModel } from '../../../data-models/user.data-model';
import {
  addManagedUserAccess,
  removeManagedUserAccess,
} from '../../../services/queries/MaggieManagedUserQueries';

export function useManageAccessViewModel(
  portfolioCompanies: ICompanyDataModel[],
  initialAccess: IManagedUserAccessDataModel[],
  userList: Array<IUserDataModel>
) {
  const sortedCompanies = useMemo(() => {
    return portfolioCompanies.sort((a, b) => a.name.localeCompare(b.name));
  }, [portfolioCompanies]);
  const sortedUsers = useMemo(() => {
    return userList
      .map((user) => {
        return {
          ...user,
          userEmail: user.email,
        };
      })
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [userList]);
  const companiesById = useMemo(() => {
    return sortedCompanies.reduce(
      (acc, company) => {
        acc[company.id] = company;
        return acc;
      },
      {} as Record<number, ICompanyDataModel>
    );
  }, [sortedCompanies]);

  const [groupByCompany, setGroupByCompany] = useState(true);
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [accessData, setAccessData] = useState(initialAccess);
  const [companyFilter, setCompanyFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  const addAccessData = async (data: IManagedUserAccessDataModel[]) => {
    let prev: IManagedUserAccessDataModel[] = [];
    setAccessData((_prev) => {
      prev = _prev;
      return [..._prev, ...data];
    });

    try {
      await addManagedUserAccess(data);
    } catch (err) {
      setAccessData(prev);
    }
  };

  const removeAccessData = async (data: IManagedUserAccessDataModel[]) => {
    let prev: IManagedUserAccessDataModel[] = [];
    setAccessData((_prev) => {
      prev = _prev;
      return _prev.filter(
        (access) =>
          !data.some((item) => item.userEmail === access.userEmail && item.companyId === access.companyId)
      );
    });

    try {
      await removeManagedUserAccess(data);
    } catch (err) {
      setAccessData(prev);
    }
  };

  const onGroupChanged = (type: 'company' | 'email') => {
    setSelectedCompanies([]);
    setSelectedUsers([]);
    setGroupByCompany(type === 'company');
  };

  const getAccessForCompany = (companyId: number) => {
    return accessData.filter((access) => access.companyId === companyId);
  };

  const getAccessForUser = (email: string) => {
    return accessData.filter((access) => access.userEmail === email);
  };

  const addUserToCompany = (email: string, companyId: number, companyName: string) => {
    const newAccess = { userEmail: email, companyId, companyName };
    addAccessData([newAccess]);
  };

  const removeUserFromCompany = (email: string, companyId: number, companyName: string) => {
    removeAccessData([{ userEmail: email, companyId, companyName }]);
  };

  const isUserInCompany = (email: string, companyId: number) => {
    return accessData.some((access) => access.userEmail === email && access.companyId === companyId);
  };

  const handleUserSingleSelect = (email: string) => {
    setSelectedUsers([email]); // Single selection - only select the clicked user
  };

  const handleUserMultiSelect = (email: string) => {
    setSelectedUsers((prev) =>
      prev.includes(email) ? prev.filter((userEmail) => userEmail !== email) : [...prev, email]
    );
  };

  const handleCompanySingleSelect = useCallback((companyId: number) => {
    setSelectedCompanies([companyId]);
  }, []);

  const handleCompanyMultiSelect = useCallback((companyId: number) => {
    setSelectedCompanies((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  }, []);

  const getAggregatedAccessStateForUser = (email: string) => {
    // For company view: check if user has access to ALL selected companies
    if (selectedCompanies.length === 0) {
      return 'none';
    }
    const hasAccessToAll = selectedCompanies.every((companyId) => isUserInCompany(email, companyId));
    const hasAccessToSome = selectedCompanies.some((companyId) => isUserInCompany(email, companyId));

    if (hasAccessToAll) {
      return 'all';
    }
    if (hasAccessToSome) {
      return 'partial';
    }
    return 'none';
  };

  const getAggregatedAccessStateForCompany = (companyId: number) => {
    // For people view: check if ALL selected users have access to the company
    if (selectedUsers.length === 0) {
      return 'none';
    }
    const allUsersHaveAccess = selectedUsers.every((userEmail) => isUserInCompany(userEmail, companyId));
    const someUsersHaveAccess = selectedUsers.some((userEmail) => isUserInCompany(userEmail, companyId));

    if (allUsersHaveAccess) {
      return 'all';
    }
    if (someUsersHaveAccess) {
      return 'partial';
    }
    return 'none';
  };

  const handleBulkAdd = (identifier: string | number) => {
    const itemsToAdd: IManagedUserAccessDataModel[] = [];

    if (groupByCompany) {
      // Add user to all selected companies
      const userEmail = identifier as string;
      selectedCompanies.forEach((companyId) => {
        if (!isUserInCompany(userEmail, companyId)) {
          const { id, name } = companiesById[companyId];
          itemsToAdd.push({
            userEmail: userEmail,
            companyId: id,
            companyName: name,
          });
        }
      });
    } else {
      // Add all selected users to the company
      const companyId = identifier as number;
      selectedUsers.forEach((userEmail) => {
        if (!isUserInCompany(userEmail, companyId)) {
          const company = sortedCompanies.find((c) => c.id === companyId);
          if (company) {
            itemsToAdd.push({ userEmail: userEmail, companyId, companyName: company.name });
          }
        }
      });
    }

    addAccessData(itemsToAdd);
  };

  const handleBulkRemove = (identifier: string | number) => {
    const itemsToRemove: IManagedUserAccessDataModel[] = [];

    if (groupByCompany) {
      // Remove user from all selected companies
      const userEmail = identifier as string;
      selectedCompanies.forEach((companyId) => {
        if (isUserInCompany(userEmail, companyId)) {
          const { id, name } = companiesById[companyId];
          itemsToRemove.push({
            userEmail: userEmail,
            companyId: id,
            companyName: name,
          });
        }
      });
    } else {
      // Remove all selected users from the company
      const companyId = identifier as number;
      selectedUsers.forEach((userEmail) => {
        if (isUserInCompany(userEmail, companyId)) {
          const company = sortedCompanies.find((c) => c.id === companyId);
          if (company) {
            itemsToRemove.push({ userEmail: userEmail, companyId, companyName: company.name });
          }
        }
      });
    }

    removeAccessData(itemsToRemove);
  };

  const filteredCompanies = sortedCompanies.filter((company) =>
    company.name.toLowerCase().includes(companyFilter.toLowerCase())
  );

  const filteredUsers = userList.filter((user) =>
    user.email.toLowerCase().includes(userFilter.toLowerCase())
  );

  const handleSelectAllCompanies = () => {
    const allCompanyIds = filteredCompanies.map((company) => company.id);
    setSelectedCompanies(allCompanyIds);
  };

  const handleSelectAllUsers = () => {
    const allUserEmails = filteredUsers.map((user) => user.email);
    setSelectedUsers(allUserEmails);
  };

  const handleDeselectAllCompanies = () => {
    setSelectedCompanies([]);
  };

  const handleDeselectAllUsers = () => {
    setSelectedUsers([]);
  };

  return {
    getAccessForCompany,
    getAccessForUser,
    addUserToCompany,
    removeUserFromCompany,
    isUserInCompany,
    handleUserSingleSelect,
    handleUserMultiSelect,
    handleCompanySingleSelect,
    handleCompanyMultiSelect,
    getAggregatedAccessStateForUser,
    getAggregatedAccessStateForCompany,
    handleBulkAdd,
    handleBulkRemove,
    handleSelectAllCompanies,
    handleSelectAllUsers,
    handleDeselectAllCompanies,
    handleDeselectAllUsers,
    groupByCompany,
    setGroupByCompany,
    selectedCompanies,
    setSelectedCompanies,
    selectedUsers,
    setSelectedUsers,
    onGroupChanged,
    sortedCompanies,
    sortedUsers,
    companyFilter,
    setCompanyFilter,
    userFilter,
    setUserFilter,
    filteredCompanies,
    filteredUsers,
  };
}

const AccessStateOrder = { all: 0, partial: 1, none: 2 };
export function compareByAccessState(
  a: { accessState: 'all' | 'partial' | 'none' },
  b: { accessState: 'all' | 'partial' | 'none' }
) {
  return AccessStateOrder[a.accessState] - AccessStateOrder[b.accessState];
}
